import type ModelRegistry from '@ember-data/types/registries/model';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type { EmberChangeset } from 'ember-changeset';
import { validatePresence } from 'ember-changeset-validations/validators';
import type { ValidatorMap } from 'ember-changeset/types';
import t from 'ember-intl/helpers/t';
import type IntlService from 'ember-intl/services/intl';
import eq from 'ember-truth-helpers/helpers/eq';
import ContinuousRange from 'parog-web/components/adverse-events/edition-form/set-level/continuous-range';
import DiscreteRange from 'parog-web/components/adverse-events/edition-form/set-level/discrete-range';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import AdverseEventDeclaredJournalEvent from 'parog-web/models/adverse-event-declared-journal-event';
import type SemeiaSessionService from 'parog-web/services/session';
import validateSometimes from 'parog-web/validators/sometimes';
import NoContentMessage from 'ui-kit/components/no-content-message';

export default class AdverseEventEditionForm extends CrudJournalEventBaseComponent {
  static override forcDisplayName = 'Symptôme';
  static override fullComponentId = 'adverse-event/edition-form';
  static override journalEventClass = AdverseEventDeclaredJournalEvent;

  @service declare intl: IntlService;
  @service declare session: SemeiaSessionService;

  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;

  get adverseEvents(): ModelRegistry['adverse-event'][] {
    return (
      this.args.patient.followUpReasonRecord?.adverseEvents ?? []
    ).slice();
  }

  get isOtherSelected(): boolean {
    return this.changeset.get('adverseEventId') == 'other';
  }

  get selectedAdverseEvent(): ModelRegistry['adverse-event'] | undefined {
    return this.adverseEvents.find(
      adverseEvent => adverseEvent.id == this.selectedAdverseEventId
    );
  }

  get selectedAdverseEventId(): string {
    return this.changeset.get('adverseEventId');
  }

  get title(): string {
    return this.intl.t('vital-sign.adverse-events.long-description');
  }

  override get validations(): ValidatorMap {
    const validations = this.journalEventClass.validations(this.intl);
    validations['otherDetails'] = validateSometimes(
      [
        validatePresence({
          presence: true,
          description: 'Le symptôme'
        })
      ],
      function (this: EmberChangeset) {
        return (this as EmberChangeset).get('adverseEventId') == 'other';
      }
    );
    validations['evaScore'] = validateSometimes(
      [
        validatePresence({
          presence: true,
          description: 'Le degré de gêne ou de douleur'
        })
      ],
      function (this: EmberChangeset) {
        return Boolean((this as EmberChangeset).get('shouldShowEva'));
      }
    );
    return validations;
  }

  override save = async () => {
    await super.save();
    this.analytics.trackPatientMeasurement('adverse-events');
  };

  selectAdverseEvent = (adverseEvent: ModelRegistry['adverse-event']): void => {
    this.changeset.set('adverseEventId', adverseEvent.id);
    this.changeset.set('shouldShowEva', true);
  };

  <template>
    <Provider @forcComponent={{this}} @title={{this.title}}>
      <:content as |Form|>
        {{#if this.session.patient}}
          <p>Que ressentez-vous&nbsp;?</p>
        {{/if}}

        {{#each this.adverseEvents as |adverseEvent|}}
          <button
            data-test-button={{adverseEvent.id}}
            class='ui button m-[0.2em] px-[1.2em] py-[0.75em] md:inline md:w-auto
              {{if (eq this.selectedAdverseEventId adverseEvent.id) "active"}}'
            type='button'
            {{on 'click' (fn this.selectAdverseEvent adverseEvent)}}
          >
            {{t (concat 'adverse-event.' adverseEvent.id)}}
          </button>
        {{/each}}

        {{#if this.isOtherSelected}}
          <div class='mt-[2em]'>
            <Form.Input
              @labelName='Précisez le symptôme'
              @propertyName='otherDetails'
            />
          </div>
        {{/if}}

        {{#if this.selectedAdverseEvent}}
          <div class='mt-[2em]'>
            <div class='field'>
              {{#if (eq this.selectedAdverseEvent.type 'discrete')}}
                <DiscreteRange
                  @form={{Form}}
                  @adverseEvent={{this.selectedAdverseEvent}}
                />
              {{else}}
                <ContinuousRange
                  @form={{Form}}
                  @adverseEvent={{this.selectedAdverseEvent}}
                />
              {{/if}}

            </div>

            <div class='field'>
              <Form.Date
                @labelName='Le symptôme est survenu le'
                @propertyName='occurredAt'
              />
            </div>
          </div>
        {{/if}}

        {{#if this.session.patient}}
          <NoContentMessage>
            <span data-test-week-end-text>
              Durant le week-end, en cas d'apparition de symptômes graves, nous
              vous invitons à contacter directement par téléphone votre équipe
              médicale.
            </span>
          </NoContentMessage>
        {{/if}}
        <div class='field'>
          <Form.TextArea @labelName='Commentaires' @propertyName='comments' />
        </div>
      </:content>
    </Provider>
  </template>
}
